// Generate the FINAL coloured Dot Studio cover PNG. Browser-only.
//
// Unlike generate-png.ts (a grayscale brightness mask re-styled by the mosaic
// at display time), this bakes the v9.1 DotEngine output — grid → dots →
// palette colours → background — straight into the PNG. The stored image IS
// the cover; the display path shows it as-is with no mosaic re-render.
//
// Variant + seed + overrides are LOCKED into the PNG at generation time.

import {
  buildDotRenderConfig,
  type DotCoverOverrides,
  renderDotStudioCover,
} from './dot-engine/cover';

export type CoverPngMimeType = 'image/png' | 'image/jpeg' | 'image/webp';

export interface GenerateCoverPngOptions {
  /** Output canvas width in pixels. Default 1200 (workspace cover size). */
  readonly width?: number;
  /** Output canvas height in pixels. Default 400 (3:1 cover at width 1200). */
  readonly height?: number;
  /** Output MIME type. Default 'image/png'. */
  readonly mimeType?: CoverPngMimeType;
  /** Compression quality (jpeg / webp only). Default 0.92. */
  readonly quality?: number;
  /** Dot Studio render overrides (spacing, element, mark, fade, …). Brand palette is fixed. */
  readonly overrides?: DotCoverOverrides;
}

const DEFAULT_WIDTH = 1200;
const DEFAULT_HEIGHT = 400;
const DEFAULT_QUALITY = 0.92;

/**
 * Render a Dot Studio cover to a fully-coloured Blob ready for upload.
 *
 * Throws if `OffscreenCanvas` isn't available (SSR / unsupported browser).
 */
export async function generateDotStudioCoverPng(
  mode: string,
  seed: number | string,
  options: GenerateCoverPngOptions = {},
): Promise<Blob> {
  const width = options.width ?? DEFAULT_WIDTH;
  const height = options.height ?? DEFAULT_HEIGHT;
  const mimeType = options.mimeType ?? 'image/png';
  const quality = options.quality ?? DEFAULT_QUALITY;

  if (typeof OffscreenCanvas === 'undefined') {
    throw new Error('pattern-studio: OffscreenCanvas not available in this runtime');
  }

  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d');
  if (ctx === null) {
    throw new Error('pattern-studio: failed to acquire 2D context on OffscreenCanvas');
  }

  const cfg = buildDotRenderConfig(mode, seed, width, height, options.overrides);
  renderDotStudioCover(ctx, cfg);

  return canvas.convertToBlob({ type: mimeType, quality });
}
